export type LinkedInNetworkDistance = 'FIRST_DEGREE' | 'SECOND_DEGREE' | 'THIRD_DEGREE' | 'OUT_OF_NETWORK';

/**
 * classic = sentinelle "pas de capacité InMail configurée"
 */
export enum LinkedInApiProduct {
  recruiter = 'recruiter',
  sales_navigator = 'sales_navigator',
  classic = 'classic',
}

export type LinkedInMessageSent = {
  chatId: string;
  messageId: string;
};

export class LinkedInProfileNotFoundError extends Error {
  extensions: { code: 'LINKEDIN_PROFILE_NOT_FOUND'; talentLinkedInSlug: string };

  constructor(message: string, talentLinkedInSlug: string) {
    super(message);
    this.name = 'LinkedInProfileNotFoundError';
    this.extensions = { code: 'LINKEDIN_PROFILE_NOT_FOUND', talentLinkedInSlug };
    Object.setPrototypeOf(this, LinkedInProfileNotFoundError.prototype);
  }
}

export type LinkedInMessagingProviderErrorDetails = {
  message: string;
  statusCode?: number;
};

export class LinkedInMessagingProviderError extends Error {
  extensions: { code: 'LINKEDIN_MESSAGING_PROVIDER_ERROR' } & LinkedInMessagingProviderErrorDetails;

  constructor(message: string, details: LinkedInMessagingProviderErrorDetails) {
    super(message);
    this.name = 'LinkedInMessagingProviderError';
    this.extensions = { code: 'LINKEDIN_MESSAGING_PROVIDER_ERROR', ...details };
    Object.setPrototypeOf(this, LinkedInMessagingProviderError.prototype);
  }
}

export class LinkedInSendMessageValidationError extends Error {
  extensions: { code: 'LINKEDIN_SEND_MESSAGE_VALIDATION_ERROR' };

  constructor(message: string) {
    super(message);
    this.name = 'LinkedInSendMessageValidationError';
    this.extensions = { code: 'LINKEDIN_SEND_MESSAGE_VALIDATION_ERROR' };
    Object.setPrototypeOf(this, LinkedInSendMessageValidationError.prototype);
  }
}

export class LinkedInNotFirstDegreeError extends Error {
  extensions: { code: 'LINKEDIN_NOT_FIRST_DEGREE'; networkDistance: Exclude<LinkedInNetworkDistance, 'FIRST_DEGREE'> };

  constructor(talentLinkedInSlug: string, networkDistance: Exclude<LinkedInNetworkDistance, 'FIRST_DEGREE'>) {
    super(`Le talent ${talentLinkedInSlug} n'est pas en relation de 1er degré (${networkDistance})`);
    this.name = 'LinkedInNotFirstDegreeError';
    this.extensions = { code: 'LINKEDIN_NOT_FIRST_DEGREE', networkDistance };
    Object.setPrototypeOf(this, LinkedInNotFirstDegreeError.prototype);
  }
}

export class LinkedInInMailInsufficientCreditsError extends Error {
  extensions: { code: 'LINKEDIN_INMAIL_INSUFFICIENT_CREDITS' };

  constructor(message: string) {
    super(message);
    this.name = 'LinkedInInMailInsufficientCreditsError';
    this.extensions = { code: 'LINKEDIN_INMAIL_INSUFFICIENT_CREDITS' };
    Object.setPrototypeOf(this, LinkedInInMailInsufficientCreditsError.prototype);
  }
}

export class LinkedInInMailInvalidAccountError extends Error {
  extensions: { code: 'LINKEDIN_INMAIL_INVALID_ACCOUNT'; linkedinApi: LinkedInApiProduct };

  constructor(message: string, linkedinApi: LinkedInApiProduct) {
    super(message);
    this.name = 'LinkedInInMailInvalidAccountError';
    this.extensions = { code: 'LINKEDIN_INMAIL_INVALID_ACCOUNT', linkedinApi };
    Object.setPrototypeOf(this, LinkedInInMailInvalidAccountError.prototype);
  }
}

export interface ILinkedInMessagingService {
  getNetworkDistance: (accountId: string, talentLinkedInSlug: string) => Promise<LinkedInNetworkDistance>;

  /**
   * Orchestrateur : résout le degré de relation, puis choisit automatiquement le mécanisme
   * d'envoi. linkedinApi === LinkedInApiProduct.classic signifie "ce compte n'a pas de capacité InMail".
   */
  sendLinkedInMessage: (
    sender: { accountId: string; linkedinApi: LinkedInApiProduct },
    talentLinkedInSlug: string,
    text: string,
  ) => Promise<LinkedInMessageSent>;
}
